import { Injectable } from '@angular/core';
import { GoogleTagManagerService as AngularGTMService } from 'angular-google-tag-manager';
import { GTMServiceFactory } from '../factory/gtm-service.factory';
import { GTMContext, GTMTag } from '../model/google-tag-manager.interface';

@Injectable({
  providedIn: 'root',
})
export class GTMService extends GTMServiceFactory {
  private context: GTMContext = {};

  constructor(private angularGTMService: AngularGTMService) {
    super();
  }

  initializeFromUserInfo(): boolean {
    try {
      const userInfoStr = localStorage.getItem('user_info');

      if (!userInfoStr) {
        return false;
      }

      const userInfo = JSON.parse(userInfoStr);

      if (!userInfo) {
        return false;
      }

      this.setContext({
        merchantId: userInfo.merchantId,
        merchantName: userInfo.merchantName,
        userRole: userInfo.role,
        userId: userInfo.uuid,
        userEmail: userInfo.email,
      });

      return true;
    } catch (error) {
      console.error('Failed to initialize GTM from user info:', error);
      return false;
    }
  }

  setContext(context: GTMContext): void {
    this.context = { ...this.context, ...context };
  }

  getContext(): GTMContext {
    return { ...this.context };
  }

  clearContext(): void {
    this.context = {};
  }

  private enrichTag(tag: Partial<GTMTag>, eventName: string): GTMTag {
    return {
      ...tag,
      event: tag.event || eventName,
      event_name: eventName,
      timestamp: tag.timestamp || Date.now(),
      user_id: tag.user_id ?? this.context.userId,
      user_email: tag.user_email ?? this.context.userEmail,
      merchant_id: tag.merchant_id ?? this.context.merchantId,
      merchant_name: tag.merchant_name ?? this.context.merchantName,
      user_role: tag.user_role ?? this.context.userRole,
    } as GTMTag;
  }

  pushTag(tag: Partial<GTMTag>): void {
    const enrichedTag = this.enrichTag(tag, tag.event_name || tag.event || 'custom_event');
    this.angularGTMService.pushTag(enrichedTag);
  }

  trackCustomEvent(
    eventName: string,
    config?: {
      category?: string;
      action?: string;
      label?: string;
      value?: any;
      additionalData?: Record<string, any>;
    }
  ): void {
    const tag = {
      event: eventName,
      category: config?.category,
      action: config?.action,
      label: config?.label,
      value: config?.value,
      ...config?.additionalData,
    };
    const enrichedTag = this.enrichTag(tag, eventName);
    this.angularGTMService.pushTag(enrichedTag);
  }

  trackPageViewed(eventName: string, pageName: string, additionalData?: Record<string, any>): void {
    const tag = {
      event: eventName,
      page_name: pageName,
      page_url: window.location.href,
      page_title: document.title,
      ...additionalData,
    };
    const enrichedTag = this.enrichTag(tag, eventName);
    this.angularGTMService.pushTag(enrichedTag);
  }
}
